'use client';

import React from 'react';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSection from '@/components/FAQSection';
import { KeepingGreen, RacingBand } from '@/components/FlyerSections';
import SupportSection from '@/components/SupportSection';
import { aboutUsFaqs } from '@/data/faqData';
import bgImg from '@/public/assets/green-gradient-blue.webp';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY } from '@/constants/typography';

/**
 * About Us Page
 * Displays company history, mission, and values
 */
export default function AboutUsPage() {
  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <main>
      <Breadcrumbs items={[{ label: 'Why Green Filter' }, { label: 'About Us' }]} />
      {/* Main Content */}
      <AboutContent $bgImg={bgImg.src}>
        <div className="container-4 w-container">
          <PageTitle>About Green Filter: High Performance USA-Made Air Filters</PageTitle>
          {/* Performance Air Filters Made in the USA */}
          <Section>
            <SectionTitle>Performance Air Filters Made in the USA</SectionTitle>
            <Paragraph>
              Green Filter USA is a performance air filter manufacturer focused on high airflow, strong filtration, and long-term reusable performance. Our filters are made in the USA and built for drivers, racers, engine builders, and automotive enthusiasts who want more from their engine air filter than a standard disposable paper filter.
            </Paragraph>
            <Paragraph>
              From daily-driven vehicles to motorsports applications, Green Filter is designed to support better airflow, reliable engine protection, easy maintenance, and lasting value.
            </Paragraph>
          </Section>

          {/* Straight From the Track to Your Vehicle */}
          <Section>
            <SectionTitle>Straight From the Track to Your Vehicle</SectionTitle>
            <Paragraph>
              Green Filter earned its reputation in performance and racing environments where airflow, filtration, durability, and consistency matter. Racing teams, engine builders, and automotive professionals need parts they can trust under demanding conditions — and that same performance-focused approach is built into every Green Filter.
            </Paragraph>
            <Paragraph>
              As Chip Ganassi said:
            </Paragraph>
            <QuoteBox>
              "We are proud to be the 2017 & 2018 back-to-back winners of the Rolex 24 Hours at Daytona and count on Green Filter to be included in our winning Ford engines."
            </QuoteBox>
            <Paragraph>
              This track-tested background helps Green Filter bring race-proven filter technology to everyday drivers and serious enthusiasts.
            </Paragraph>
          </Section>

          {/* Our Story */}
          <Section>
            <SectionTitle>Our Story</SectionTitle>
            <Paragraph>
              Green Filter USA traces its roots to 2001, when founder Mark German used his racing experience, parts manufacturing background, and passion for vehicle performance to create an air filter built for better airflow and dependable filtration.
            </Paragraph>
            <Paragraph>
              Mark understood that performance drivers needed a filter that could help support horsepower while still capturing harmful particles across tough racing and driving environments. That idea became the foundation of Green Filter: a washable, reusable, high-performance air filter built for power, protection, and long-term use.
            </Paragraph>
          </Section>

          {/* Built by a Green Filter USA Manufacturer */}
          <Section>
            <SectionTitle>Built by a Green Filter USA Manufacturer</SectionTitle>
            <Paragraph>
              As a Green Filter USA manufacturer, our focus is on quality from design to production. Our team listens to racing professionals, performance builders, and real drivers to improve fitment, construction, airflow, durability, and filter maintenance.
            </Paragraph>
            <Paragraph>
              Green Filter products are engineered for modern vehicles and performance applications, including cars, trucks, motorcycles, custom intakes, and racing setups. We build filters that are designed to perform while supporting today's advanced engine monitoring systems.
            </Paragraph>
          </Section>

          {/* Why Drivers Choose Green Filter */}
          <Section>
            <SectionTitle>Why Drivers Choose Green Filter</SectionTitle>
            <Paragraph>
              Green Filter is built for people who care about performance, reliability, and long-term value.
            </Paragraph>
            <Paragraph>Key benefits include:</Paragraph>
            <BenefitsList>
              <li>High-flow performance design</li>
              <li>Washable and reusable filter media</li>
              <li>Made in the USA quality</li>
              <li>Designed for reliable filtration</li>
              <li>Built for easy cleaning and maintenance</li>
              <li>Supports improved throttle response and airflow</li>
              <li>Long-term value compared to disposable paper filters</li>
              <li>Designed for daily driving, enthusiast builds, and motorsports use</li>
            </BenefitsList>
          </Section>

          {/* Made for Enthusiasts, Racers, and Everyday Drivers */}
          <Section>
            <SectionTitle>Made for Enthusiasts, Racers, and Everyday Drivers</SectionTitle>
            <Paragraph>
              The Green Filter family is made up of automotive enthusiasts who understand the passion behind every build. Whether you are looking for better airflow, improved performance, easier maintenance, or a reusable air filter that lasts, Green Filter is built to support your goals.
            </Paragraph>
            <Paragraph>
              We continue to expand our product line based on customer feedback, real vehicle needs, and performance applications. If you do not see the exact filter for your make, model, or custom setup, our team can help you find the right option.
            </Paragraph>
          </Section>

          {/* Our Commitment */}
          <Section>
            <SectionTitle>Our Commitment</SectionTitle>
            <Paragraph>
              Green Filter is committed to building high-quality performance air filters that drivers can trust. Our mission is simple: deliver USA-made air filters that combine airflow, filtration, reusability, and long-term performance.
            </Paragraph>
            <Paragraph>
              From the track to the street, Green Filter continues to support enthusiasts, racers, builders, and everyday drivers who want dependable performance from a filter made with care.
            </Paragraph>
          </Section>

          {/* CTA */}
          <SupportSection
            text="Ready to upgrade your air filter? Shop Green Filter by vehicle, browse our universal filters, or contact our team for help finding the right filter for your application."
            primaryButtonText="Shop by Vehicle"
            primaryButtonLink="/shop-by-vehicle"
            secondaryButtonText="Browse Universal Filters"
            secondaryButtonLink="/universal-cylinder"
          />
        </div>
      </AboutContent>

      {/* Brochure: racing story and environment */}
      <RacingBand hideButton />
      <KeepingGreen />

      {/* FAQ Section */}
      <FAQSection faqs={aboutUsFaqs} title="FAQs About Green Filter USA" />
      </main>

      <Footer />
    </>
  );
}

// Styled Components
const AboutContent = styled.div<{ $bgImg: string }>`
  position: relative;
  margin-top: 0%;
  padding: 5% 5% 8%;
  background-color: #fff;
  background-image: url(${(props) => props.$bgImg});
  background-position: 50% 100%;
  background-size: contain;
  background-repeat: no-repeat;

  @media screen and (max-width: 767px) {
    padding: 8% 5%;
  }
`;

const Section = styled.section`
  margin-bottom: 48px;
  text-align: left;

  @media screen and (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const PageTitle = styled.h1`
  ${TYPOGRAPHY.h1}
  color: ${COLORS.textPrimary};
  margin: 0 0 24px;

  @media screen and (max-width: 767px) {
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px;
  line-height: 1.3;

  @media screen and (max-width: 767px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`;

const Paragraph = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  margin: 0 0 16px;

  @media screen and (max-width: 767px) {
    font-size: 15px;
  }
`;

const QuoteBox = styled.blockquote`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 500;
  font-style: italic;
  color: #1a1a1a;
  line-height: 1.6;
  margin: 24px 0;
  padding: 24px 32px;
  background: #f8f9fa;
  border-left: 4px solid ${COLORS.brandGreen};
  border-radius: 4px;

  @media screen and (max-width: 767px) {
    font-size: 16px;
    padding: 20px 24px;
    margin: 20px 0;
  }
`;

const BenefitsList = styled.ul`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  margin: 16px 0 0 24px;
  padding: 0;

  li {
    margin-bottom: 12px;
    padding-left: 8px;

    &::marker {
      color: ${COLORS.brandGreen};
    }
  }

  @media screen and (max-width: 767px) {
    font-size: 15px;
    margin-left: 20px;
  }
`;

